import { FileText } from 'lucide-react';
import type { AssistantMissingDocsData, Redactable } from '@cumpli/shared';
import { Pill } from '../../ui';
import { CardHeader, CardShell, CiteChip } from './shell';
import { MaybeRedacted, isRedacted } from './redacted';
import { docToneOf } from './labels';

/** One document row: file icon, name, and a status pill on the right. */
function DocRow({
  name,
  children,
}: {
  name: Redactable<string>;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg bg-canvas px-2.5 py-2">
      <FileText className="h-4 w-4 shrink-0 text-ink-faint" strokeWidth={1.75} />
      <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
        <MaybeRedacted value={name} />
      </span>
      <span className="shrink-0">{children}</span>
    </div>
  );
}

/**
 * `list_missing_documents` — what's missing or expiring on an expediente.
 * Missing docs read amber "No cargado"; expiring/present-with-issue docs carry
 * their estado tone; a fully complete file collapses to one green pill.
 */
export function MissingDocsCard({ data }: { data: AssistantMissingDocsData }) {
  const nothingPending = data.missing.length === 0 && data.expiring.length === 0;
  return (
    <CardShell>
      <CardHeader
        title={<MaybeRedacted value={data.nombre} />}
        label="Documentos"
      />

      {nothingPending ? (
        <div className="mt-2.5">
          <Pill tone="green">Expediente completo</Pill>
        </div>
      ) : (
        <div className="mt-2.5 space-y-1.5">
          {data.missing.map((doc, i) => (
            <DocRow key={`m-${i}`} name={doc}>
              <Pill tone="amber">No cargado</Pill>
            </DocRow>
          ))}
          {data.expiring.map((d, i) => (
            <DocRow key={`e-${i}`} name={d.category}>
              <Pill tone={isRedacted(d.estado) ? 'slate' : docToneOf(d.estado)}>
                <MaybeRedacted value={d.estado} />
              </Pill>
            </DocRow>
          ))}
        </div>
      )}

      <CiteChip>Fundamento legal · Ley 23 de 2015</CiteChip>
    </CardShell>
  );
}
